/**
 * Pure helpers for catalogue `category` array construction.
 *
 * Extracted from `catalogue/detail.ts` save handler so the
 * defensive-default logic for `category[0]` (the
 * observation-category marker) is testable in isolation.
 *
 * Filed against OMON-089 (test additions for OMON-079 review surface)
 * + pins the PR #435363 sparse-array regression that surfaced post-
 * OMON-079 v4 reseed.
 */

export const OBSERVATION_CATEGORY_SYSTEM =
  "https://sweettreeconnect.com/fhir/observation-category";
export const GOAL_CATEGORY_SYSTEM =
  "https://sweettreeconnect.com/fhir/CodeSystem/goal-category";
export const GOAL_SUBCATEGORY_SYSTEM =
  "https://sweettreeconnect.com/fhir/CodeSystem/goal-subcategory";
export const TRACKABLE_CAPABILITY_SYSTEM =
  "https://sweettreeconnect.com/fhir/CodeSystem/trackable-capability";

/**
 * Resolve an `_observationDefinition.category[]` array into role-keyed codes.
 *
 * Per ADR-0007 and ADR-0015: category entries are identified by their
 * `coding[].system` URI, NOT by their array index. Position-based readers
 * (e.g. `category[1]`) broke when OMON-134 migration collapsed the array
 * for monitored-event ODs (which have a 2-entry [observation-category,
 * goal-subcategory] shape vs developmental goals' 3-entry shape).
 *
 * Use this resolver any time you need to extract a code from a category
 * array. Returns the first matching entry's code per role (categories rarely
 * have more than one entry per role, but we defensively take the first).
 *
 * For developmental ODs: returns all three roles (observationCategory,
 * goalCategory, goalSubcategory) populated.
 *
 * For monitored-event ODs (post-OMON-134): returns observationCategory
 * = "monitored-event" and goalSubcategory populated; goalCategory is undefined
 * because monitored events don't carry a goal-category entry (the
 * observation-category itself is the discriminator).
 */
export function resolveCategoryCodes(
  category: unknown,
): {
  observationCategory?: string;
  goalCategory?: string;
  goalSubcategory?: string;
} {
  if (!Array.isArray(category)) return {};
  const out: { observationCategory?: string; goalCategory?: string; goalSubcategory?: string } = {};
  for (const entry of category) {
    if (!entry || typeof entry !== "object") continue;
    const coding = (entry as { coding?: unknown }).coding;
    if (!Array.isArray(coding)) continue;
    for (const c of coding) {
      if (!c || typeof c !== "object") continue;
      const system = (c as { system?: string }).system;
      const code = (c as { code?: string }).code;
      if (!system || !code) continue;
      if (system === OBSERVATION_CATEGORY_SYSTEM && !out.observationCategory) {
        out.observationCategory = code;
      } else if (system === GOAL_CATEGORY_SYSTEM && !out.goalCategory) {
        out.goalCategory = code;
      } else if (system === GOAL_SUBCATEGORY_SYSTEM && !out.goalSubcategory) {
        out.goalSubcategory = code;
      }
    }
  }
  return out;
}

export interface BuildCategoryArrayInput {
  /** Existing `category` array on the doc, or `[]` if absent. */
  prior: unknown[];
  /** Stable Goal Category code (e.g. "personal-care"). */
  categoryCode: string;
  /** Stable Goal Sub-category code (e.g. "dental-hygiene"). */
  subcategoryCode: string;
  /** Current display string for the Goal Category (denormalised hint). */
  categoryDisplay: string;
  /** Current display string for the Goal Sub-category (denormalised hint). */
  subcategoryDisplay: string;
}

/**
 * Rebuild the FHIR `category` array for an Observation/Activity Definition
 * write, matched **by `coding[].system` URI, not by array index** (OMON-166).
 *
 * The old positional version rewrote `category[1]` (Goal Category) and
 * `category[2]` (Goal Sub-category) in place. After the Trackable v3 curation
 * (2026-06-08) the `trackable-capability` (D/M/S) codings live at arbitrary
 * indices — so a positional rewrite *dropped the capability codings*. This
 * version instead:
 *   - **preserves every entry that is not** a goal-category / goal-subcategory
 *     coding (capability codings, the observation-category marker, anything else);
 *   - re-authors the goal-category entry;
 *   - re-authors the goal-subcategory entry **only when a sub-category code is
 *     given** — facts collapsed by the 2026-06-08 layer-redundancy pass carry no
 *     sub-category, so we omit the entry entirely rather than writing an empty one;
 *   - seeds an observation-category marker only if none survives (the
 *     PR #435363 empty-array contract; harmless, append-only).
 */
function entryHasSystem(entry: unknown, system: string): boolean {
  const coding = (entry as { coding?: unknown } | null)?.coding;
  if (!Array.isArray(coding)) return false;
  return coding.some((c) => c && typeof c === "object" && (c as { system?: string }).system === system);
}

export function buildCategoryArray(input: BuildCategoryArrayInput): unknown[] {
  const out: unknown[] = (input.prior || []).filter(
    (e) =>
      e != null &&
      !entryHasSystem(e, GOAL_CATEGORY_SYSTEM) &&
      !entryHasSystem(e, GOAL_SUBCATEGORY_SYSTEM),
  );
  if (!out.some((e) => entryHasSystem(e, OBSERVATION_CATEGORY_SYSTEM))) {
    out.unshift({ coding: [{ system: OBSERVATION_CATEGORY_SYSTEM, code: "outcome-assessment" }] });
  }
  out.push({
    coding: [{ system: GOAL_CATEGORY_SYSTEM, code: input.categoryCode, display: input.categoryDisplay }],
  });
  if (input.subcategoryCode) {
    out.push({
      coding: [{ system: GOAL_SUBCATEGORY_SYSTEM, code: input.subcategoryCode, display: input.subcategoryDisplay }],
    });
  }
  return out;
}

/**
 * Replace the `trackable-capability` (D/M/S) codings on a `category[]` array
 * with the given set, preserving every other entry (goal-category,
 * goal-subcategory, observation-category, …). Matched by system, not index
 * (OMON-166). Capability codes are appended after the kept entries.
 */
export function setCapabilityCodings(category: unknown[], capabilities: string[]): unknown[] {
  const kept = (category || []).filter((e) => e != null && !entryHasSystem(e, TRACKABLE_CAPABILITY_SYSTEM));
  const caps = capabilities.map((code) => ({
    coding: [{
      system: TRACKABLE_CAPABILITY_SYSTEM,
      code,
      display: code.charAt(0).toUpperCase() + code.slice(1),
    }],
  }));
  return [...kept, ...caps];
}
